// Library methods
import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { DateTime } from "luxon";

// Utilities
import { addExam as examAdd } from "../../services/Exam";
import { examCardEnums } from "../../enums";
import useClinic from "../useClinic";

export const useDefaultExams = () => {
  const { clinicId, clinicTimezone } = useClinic();
  const { getAccessTokenSilently } = useAuth0();

  const addDefaultExamData = useCallback(
    async ({ sectionTypeOrder, examSections, patientId }) => {
      const today = DateTime.now()
        .setZone(clinicTimezone)
        .startOf("day")
        .toUTC()
        .toISO();

      const examData = {
        sectionTypeOrder: sectionTypeOrder.join(", "),
        scheduledDate: today,
        ...examSections,
      };

      // add exam to exams api
      let newExam = null;

      try {
        const token = await getAccessTokenSilently();
        newExam = await examAdd(token, clinicId, patientId, examData);
      } catch (e) {
        console.error(e);
      } finally {
        return newExam;
      }
    },
    [getAccessTokenSilently, clinicId, clinicTimezone]
  );

  const getFormattedExamSections = useCallback(({ defaultExam }) => {
    let examSections = {};

    const sectionNames = Object.values(examCardEnums).map(
      (value) => value.sectionName
    );
    sectionNames.forEach((sectionName) => {
      if (
        defaultExam?.[sectionName] &&
        defaultExam?.[sectionName]?.length > 0
      ) {
        examSections[sectionName] = [...defaultExam[sectionName]];
      }
    });

    return examSections;
  }, []);

  const addDefaultExam = useCallback(
    async ({ patientId, predefinedExam, onAddDefaultExamCallback }) => {
      const examContent = JSON.parse(predefinedExam?.content);
      const examSections = getFormattedExamSections({
        defaultExam: examContent,
      });

      const newExam = await addDefaultExamData({
        sectionTypeOrder: examContent?.sectionTypeOrder,
        examSections,
        patientId,
      });
      await onAddDefaultExamCallback(newExam);
    },
    [getFormattedExamSections, addDefaultExamData]
  );

  return {
    addDefaultExam,
  };
};
